import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { KpiAccent } from './KpiCard'

/** Solid-color tile backgrounds (as opposed to KpiCard's light badge-on-white
 * treatment) -- same accent names/meanings as KpiCard's ACCENT_STYLES, kept
 * in sync manually since this is a small, fixed palette. */
const TILE_STYLES: Record<KpiAccent, string> = {
  terracotta: 'bg-terracotta-600',
  green: 'bg-green-600',
  blue: 'bg-blue-600',
  purple: 'bg-purple-600',
  red: 'bg-red-600',
  amber: 'bg-amber-600',
}

export interface StatTile {
  icon: LucideIcon
  value: ReactNode
  label: string
  subtext?: ReactNode
  accent: KpiAccent
}

/**
 * Horizontal strip of solid-color tiles, each with an icon + value, and its
 * label below the tile -- inspired by .guides/PlantillaKPIs.png's bottom
 * strip of colored stat tiles, kept in this app's own palette (a distinct
 * accent per tile, rather than the template's single blue-to-cyan gradient).
 * Meant for a small, fixed set of related metrics (e.g. data-quality/
 * coverage figures), not a general-purpose KPI row -- use KpiCard for that.
 */
export default function StatTileStrip({ tiles }: { tiles: StatTile[] }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {tiles.map((tile, index) => (
        <div key={index} className="flex flex-col items-center text-center gap-2">
          <div className={`flex flex-col items-center justify-center gap-1 w-full rounded-lg py-4 text-white shadow ${TILE_STYLES[tile.accent]}`}>
            <tile.icon className="h-5 w-5" aria-hidden="true" />
            <span className="text-xl font-bold">{tile.value}</span>
          </div>
          <div className="text-xs font-medium text-espresso-600">{tile.label}</div>
          {tile.subtext && <div className="text-[11px] text-espresso-400">{tile.subtext}</div>}
        </div>
      ))}
    </div>
  )
}
